import { jObjectLittle, jObjectLittleSchema } from './objectBase';
import { z, ZodType } from 'zod';
import { formatListSchema } from './utils';

export const searchCategoryValueSchema = z.object({
    count: z.number(),
    id: z.string(),
});
export type SearchCategoryValue = z.infer<typeof searchCategoryValueSchema>;

export const facetModeSchema = z.union([z.literal('AND'), z.literal('OR')]);
export type FacetMode = z.infer<typeof facetModeSchema>;

export const searchFacetModelSchema = z.object({
    facetType: z.string(),
    facetOptions: z.union([
        z.record(z.union([z.object({}).passthrough(), z.number(), z.string()])),
        z.array(z.never()),
    ]),
    facetMode: facetModeSchema.optional(),
    field: z.string(),
    title: z.string(),
    description: z.string().optional(),
    category: z.string().optional(),
});
export type SearchFacetModel = z.infer<typeof searchFacetModelSchema>;

export const searchCategoryModelSchema = z.object({
    id: z.string(),
    mainType: z.string(),
    type: z.string().optional(),
    title: z.string(),
    cssclass: z.string().optional(),
    active: z.boolean().optional(),
    count: z.number().optional(),
    typesExcluded: z.array(z.string()),
    facets: searchFacetModelSchema.array(),
});
export type SearchCategoryModel = z.infer<typeof searchCategoryModelSchema>;

export const searchFacetValuesSchema = z.object({
    count: z.number(),
    value: z.string(),
    title: z.string(),
});
export type SearchFacetValues = z.infer<typeof searchFacetValuesSchema>;

export const searchFacetValueModelSchema = z.object({
    name: z.string(),
    count: z.number(),
    values: searchFacetValuesSchema.array(),
    title: z.string(),
});
export type SearchFacetValueModel = z.infer<typeof searchFacetValueModelSchema>;

/** *
 * Used in search query to specify a filter
 * field : name/id of the filter category (ie skills)
 * value : id of the field (ie react)
 * exclude : boolean to include or exclude the filter
 */
export const searchQueryFilterModelSchema = z.object({
    field: z.string(),
    value: z.union([z.string(), z.array(z.string())]),
    exclude: z.boolean().optional(),
    or: z.boolean().optional(),
});
export type SearchQueryFilterModel = z.infer<typeof searchQueryFilterModelSchema>;

/**
 * Used in search query to specify a filter
 * field : name/id of the field to sort
 * order : desc' | 'asc'
 */
export const searchQuerySortModelSchema = z.object({
    field: z.string(),
    order: z.union([z.literal('desc'), z.literal('asc')]),
});
export type SearchQuerySortModel = z.infer<typeof searchQuerySortModelSchema>;

/**
 * facetLimit : max number of facets (default 25)
 * skipEmptyFacets : remove empty facets (with 0 results)
 */
export const searchQueryOptionSchema = z.object({
    facetLimit: z.number(),
    skipEmptyFacets: z.boolean(),
});
export type SearchQueryOption = z.infer<typeof searchQueryOptionSchema>;

export const searchConfigSchema = z.object({
    home: z.object({
        mode: z.union([z.literal('result'), z.literal('category')]),
        facets: searchFacetModelSchema.array(),
    }),
    categories: searchCategoryModelSchema.array(),
    viewcard: z.record(
        z.array(
            z.object({
                name: z.string(),
                title: z.string(),
            }),
        ),
    ),
    facetMode: z.boolean(),
});
export type SearchConfig = z.infer<typeof searchConfigSchema>;

export const searchResultPropsAppSchema = z.object({
    name: z.string(),
    title: z.string(),
    img: z.string(),
    icon: z.string().optional(),
    color: z.string().optional(),
    urlTarget: z.enum(['_blank']).optional(),
    urlNoReferrer: z.boolean().optional(),
    _url: z.string(),
});
export type SearchResultPropsApp = z.infer<typeof searchResultPropsAppSchema>;

// TODO this need to be improve to have a basic type with the category of search return passed in extend
export const searchResultPropsObjectSchema = z.object({
    id: z.number().optional(),
    title: z.string(),
    picture: z.string().optional(),
    uri: z.string(),
    mainType: z.string(),
    mimetype: z.string().optional(),
    parentIdInType: z.number().optional(),
    parentType: z.string().optional(),
    privacy: z.string().optional(),
    type: z.string().optional(),
    icon: z.string().optional(),
    _cssColor: z.string().optional(),
    _cssClass: z.string().optional(),
    _url: z.string().optional(),
    _article: z.object({ id: z.string(), mainType: z.string() }).optional(),
    idArticle: z.number().optional(),
    description: z.string().optional(),
    _user: z
        .object({
            title: z.string(),
            uri: z.string(),
        })
        .optional(),
    _values: z
        .object({
            company: z.string().optional(),
            function: z.string().optional(),
            privacy: z.number().optional(),
            category: z.string().optional(),
            dateCreation: z.string(),
            dateModified: z.string(),
        })
        .optional(),
});
export type SearchResultPropsObject = z.infer<typeof searchResultPropsObjectSchema>;

export const searchQuerySchema = z.object({
    keywords: z.string().optional(),
    category: z.string().optional(),
    facetCategories: z.boolean().optional(),
    filters: searchQueryFilterModelSchema.array().optional(),
    facets: searchFacetModelSchema.array().optional(),
    page: z.number().optional(),
    limit: z.number().optional(),
    sort: searchQuerySortModelSchema.optional(),
    options: searchQueryOptionSchema.optional(),
    format: formatListSchema.optional(),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

export const inferSearchQueryResultSchema = <T extends ZodType<jObjectLittle> = ZodType<jObjectLittle>>(type?: T) => {
    return z.object({
        start: z.number(),
        count: z.number(),
        results: z.array(type ?? jObjectLittleSchema),
        facets: searchFacetValueModelSchema.array(),
        categories: searchCategoryValueSchema.array(),
    });
};
export type SearchQueryResult<T extends jObjectLittle = jObjectLittle> = z.infer<
    ReturnType<typeof inferSearchQueryResultSchema<ZodType<T>>>
>;

export const searchLastQuerySchema = z.object({
    keywords: z.string(),
});
export type SearchLastQuery = z.infer<typeof searchLastQuerySchema>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SearchAdminConfig = any;

export const searchResultPropsSchema = z.union([searchResultPropsAppSchema, searchResultPropsObjectSchema]);
export type SearchResultProps = z.infer<typeof searchResultPropsSchema>;

export const searchResultCatPropsSchema = z.object({
    title: z.string(),
    id: z.string(),
    showAll: z.boolean().optional(), // FIXME rename to indexed (back + front)
    results: searchResultPropsSchema.array(),
});
export type SearchResultCatProps = z.infer<typeof searchResultCatPropsSchema>;
